/* UGC.
 *
 * Drives the video tiles on the `ugc` section: what plays, when, and whether
 * the page has sound.
 *
 * The section renders a poster image for every tile and the <video> sits over
 * it at opacity 0, so with this file absent the row is a set of stills and
 * nothing is broken or blank. Only the motion needs script.
 *
 * TWO RULES, and everything here follows from them (Tim's review Loom,
 * 17 Sep, which corrected the first build on both counts):
 *
 * 1. EXACTLY ONE TILE PLAYS AT A TIME, page-wide. The first build let an
 *    unmuted tile keep playing while the pointer moved to another, so two
 *    videos ran at once. There is no "held" tile any more: starting one stops
 *    every other one, in this section and in any other on the page.
 *
 * 2. SOUND IS ONE GLOBAL SWITCH, not a per-tile state. Clicking any chip, or
 *    anywhere on any video, flips sound for the whole page, every chip label
 *    updates together, and whichever tile plays next inherits it. The first
 *    build made it per-tile, which meant four independent states and a chip
 *    whose label only described the one tile under the pointer.
 *
 * 3. THE ROW IS NEVER STILL (Tim's second review, 17 Sep). One tile plays from
 *    the moment the section scrolls into view and they take it in turns;
 *    hovering only changes WHICH one. Leaving a tile resumes the rotation FROM
 *    that tile rather than from wherever it had got to, so the row carries on
 *    where the visitor was looking. The first build had the rotation off by
 *    default and played nothing until you hovered.
 *
 * Which tile plays is per input:
 *
 *   desktop  the rotation, or the hovered tile while there is one
 *   touch    whichever tile is in view. No hover to rotate around, and only
 *            one tile is on screen at a time in a peeking track, so a rotation
 *            would be playing something the visitor cannot see
 *
 * Autoplay is only ever granted to a MUTED video, so a tile always starts
 * muted and is unmuted only once the visitor has clicked. Even then a browser
 * can refuse, so an unmuted play that rejects falls back to muted rather than
 * leaving a dead tile.
 *
 * Under `prefers-reduced-motion` nothing auto-plays and the chip becomes a
 * plain play / pause, which is the only mode where a tile plays because the
 * visitor asked rather than because they arrived.
 *
 * The section emits its own <script src> tag, so a page with two of these asks
 * for the file twice. The browser dedupes the fetch; this guard dedupes the
 * execution.
 */
(function () {
  if (window.__ddjUgc) return;
  window.__ddjUgc = true;

  var SELECTOR = '[data-ugc]';

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  var hoverable = window.matchMedia('(hover: hover) and (pointer: fine)');

  /* Every tile on the page, across sections. Both rules above are page-wide,
   * so they need one list rather than one per section. */
  var all = [];

  /* Rule 2: one switch for the whole page. Starts off, because it has to. */
  var soundOn = false;

  function isReduced() {
    return reduced.matches;
  }

  /* ---------------------------------------------------------------- tile */

  function Tile(el) {
    this.el = el;
    this.video = el.querySelector('video');
    this.chip = el.querySelector('[data-ugc-chip]');
    this.well = el.querySelector('[data-ugc-well]');
  }

  Tile.prototype.ready = function () {
    return !!this.video;
  };

  /* Play this tile with the page's current sound setting, falling back to
   * muted if the browser refuses the unmuted play. */
  Tile.prototype.play = function () {
    if (!this.video) return;
    var video = this.video;
    var el = this.el;

    video.muted = !soundOn;
    el.dataset.ugcPlaying = 'true';

    var attempt = video.play();
    if (!attempt || typeof attempt.catch !== 'function') return;

    attempt.catch(function () {
      /* An unmuted play with no sticky activation behind it is refused. Retry
       * muted so the tile still moves: a silent video beats a frozen one, and
       * the chip is still there to ask again. */
      if (video.muted) {
        el.dataset.ugcPlaying = 'false';
        return;
      }
      video.muted = true;
      var retry = video.play();
      if (retry && typeof retry.catch === 'function') {
        retry.catch(function () {
          el.dataset.ugcPlaying = 'false';
        });
      }
    });
  };

  Tile.prototype.pause = function () {
    if (!this.video) return;
    this.video.pause();
    this.el.dataset.ugcPlaying = 'false';
  };

  /* Pause AND rewind. Leaving a tile part-played means the next hover picks up
   * mid-shot, which reads as a glitch rather than a loop. */
  Tile.prototype.stop = function () {
    if (!this.video) return;
    this.pause();
    try {
      this.video.currentTime = 0;
    } catch (e) {
      /* Seeking before any metadata has loaded throws in Safari. Nothing to
       * rewind in that case, so there is nothing to recover either. */
    }
  };

  Tile.prototype.playing = function () {
    return !!this.video && !this.video.paused;
  };

  /* -------------------------------------------------------------- page */

  /* Rule 1. The only way a tile is ever started. */
  function playOnly(tile) {
    for (var i = 0; i < all.length; i++) {
      if (all[i] !== tile) all[i].stop();
    }
    if (tile) tile.play();
  }

  /* Rule 2. One switch, every chip, and whatever is playing right now. */
  function setSound(on) {
    soundOn = on;
    for (var i = 0; i < all.length; i++) {
      var tile = all[i];
      if (tile.video) tile.video.muted = !soundOn;
      tile.el.dataset.ugcSound = soundOn ? 'on' : 'off';
      if (tile.chip) {
        var label = tile.chip.dataset[soundOn ? 'ugcLabelUnmuted' : 'ugcLabelMuted'];
        if (label) tile.chip.textContent = label;
        tile.chip.setAttribute('aria-pressed', soundOn ? 'true' : 'false');
      }
    }
  }

  /* ------------------------------------------------------------- section */

  function init(root) {
    if (root.dataset.ugcReady === 'true') return;
    root.dataset.ugcReady = 'true';

    var mode = root.dataset.ugcPlayback || 'auto';
    var chipMode = root.dataset.ugcChip || 'sound';

    var tiles = [];
    var nodes = root.querySelectorAll('[data-ugc-tile]');
    for (var i = 0; i < nodes.length; i++) {
      var tile = new Tile(nodes[i]);
      if (tile.ready()) {
        tiles.push(tile);
        all.push(tile);
      }
    }
    if (!tiles.length) return;

    /* Reduced motion downgrades the section to click-to-play whatever the
     * merchant chose, and the chip follows: a sound toggle on a video that
     * never starts has nothing to toggle. */
    var autoplays = mode !== 'poster' && !isReduced();
    var chipPlays = chipMode === 'play' || mode === 'poster' || isReduced();

    setSound(soundOn);

    /* -------------------------------------------------------- the chip */

    function togglePlay(tile) {
      if (tile.playing()) {
        tile.pause();
      } else {
        playOnly(tile);
      }
    }

    function act(tile) {
      if (chipPlays) {
        togglePlay(tile);
        return;
      }

      setSound(!soundOn);

      /* The click is the gesture that lets an unmuted play through, so if
       * this tile is not the one running, hand it the sound by making it the
       * one that is. */
      if (soundOn && !tile.playing()) playOnly(tile);
    }

    tiles.forEach(function (tile) {
      if (tile.chip) {
        tile.chip.addEventListener('click', function (event) {
          event.preventDefault();
          event.stopPropagation();
          act(tile);
        });
      }

      /* Tim, 17 Sep: the whole well is the target, not just the chip. The
       * credit link is a real anchor, so it keeps its own click. */
      if (tile.well) {
        tile.well.addEventListener('click', function (event) {
          if (event.target.closest('a')) return;
          act(tile);
        });
      }
    });

    if (!autoplays) return;

    /* ------------------------------ round robin + hover, desktop ------
     *
     * Tim, 17 Sep (second review): the row is never still. One tile plays
     * from the moment the section scrolls into view, they take it in turns,
     * and hovering only ever changes WHICH one.
     *
     * Hovering interrupts the rotation and plays what is under the pointer.
     * Leaving does not hand the turn back to where the rotation had got to:
     * it resumes FROM the tile just left, so the row carries on from where
     * the visitor was looking rather than jumping somewhere else.
     */

    if (hoverable.matches && mode !== 'hover-only') {
      var current = 0;
      var running = false;
      var hovered = null;

      /* Looping and taking turns are contradictory: a looping tile never
       * fires `ended`, so the row would stop on tile one. A lone tile has no
       * turn to pass on, so it keeps its loop. */
      var rotates = tiles.length > 1;

      function advance(from) {
        current = (from + 1) % tiles.length;
        playOnly(tiles[current]);
      }

      tiles.forEach(function (tile, index) {
        if (rotates) tile.video.loop = false;

        tile.video.addEventListener('ended', function () {
          if (!running || !rotates) return;
          /* Still under the pointer: replay it rather than moving on, which
           * is the loop the visitor expects while they are looking at it. */
          if (hovered === tile) {
            playOnly(tile);
            return;
          }
          advance(index);
        });

        tile.el.addEventListener('mouseenter', function () {
          hovered = tile;
          playOnly(tile);
        });

        tile.el.addEventListener('mouseleave', function () {
          if (hovered !== tile) return;
          hovered = null;
          /* Resume the rotation AT this tile, from its start. */
          current = index;
          if (running) playOnly(tile);
          else tile.stop();
        });
      });

      var sectionSeen = new IntersectionObserver(
        function (entries) {
          running = entries[0].isIntersecting;
          if (running) {
            playOnly(hovered || tiles[current]);
          } else {
            /* Offscreen tiles decode for nobody. */
            tiles.forEach(function (tile) {
              tile.pause();
            });
          }
        },
        { threshold: 0.2 }
      );
      sectionSeen.observe(root);
    }

    /* --------------------------------------- hover only, desktop ---- */

    if (hoverable.matches && mode === 'hover-only') {
      tiles.forEach(function (tile) {
        tile.el.addEventListener('mouseenter', function () {
          playOnly(tile);
        });
        tile.el.addEventListener('mouseleave', function () {
          tile.stop();
        });
      });
    }

    /* ------------------------------------------------ in view, touch */

    if (!hoverable.matches) {
      /* No hover to rotate around, and only one tile is on screen at a time
       * in a peeking track, so the rotation would be playing something the
       * visitor cannot see. The tile in view is the tile that plays. */
      var seen = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            var tile = null;
            for (var i = 0; i < tiles.length; i++) {
              if (tiles[i].el === entry.target) tile = tiles[i];
            }
            if (!tile) return;
            if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
              playOnly(tile);
            } else {
              tile.stop();
            }
          });
        },
        /* 0.6 of a tile, against a track that shows 1.25 of them, means one
         * tile qualifies at a time without needing to rank them. */
        { threshold: [0, 0.6] }
      );
      tiles.forEach(function (tile) {
        seen.observe(tile.el);
      });
    }
  }

  function initAll(scope) {
    var roots = (scope || document).querySelectorAll(SELECTOR);
    for (var i = 0; i < roots.length; i++) init(roots[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }

  /* Theme editor: a re-rendered section arrives with `data-ugc-ready` stripped.
   * Its tiles are new elements, so drop the old ones from the page-wide list
   * or `playOnly` keeps trying to stop nodes that are no longer in the DOM. */
  document.addEventListener('shopify:section:load', function (event) {
    all = all.filter(function (tile) {
      return document.contains(tile.el);
    });
    initAll(event.target);
  });
})();
